// src/store/slices/branchSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  createBranchThunk,
  getBranchesThunk,
  getAllBranchesThunk,
  getBranchThunk,
  updateBranchThunk,
  configureRateModeThunk,
  setMainBranchThunk,
  copyConfigurationThunk,
  deactivateBranchThunk,
  getBranchAnalyticsThunk,
  getBranchHierarchyThunk,
  createStoreThunk,
  getBranchStoresThunk,
  updateStoreThunk,
  deleteStoreThunk,
  transferStoreThunk,
  getStoreMetricsThunk,
  bulkUpdateBranchesThunk,
  bulkDeactivateBranchesThunk,
  clearBranchError,
  resetSelectedBranch,
  resetStoreData,
  configureHybridPolicyThunk,
  validateInheritanceThunk,
  getInheritanceSourcesThunk,
  getInheritanceChainThunk,
  getDependentBranchesThunk,
  clearInheritanceValidation,
  setInheritanceSource,
  updateAdminPrimaryBranchThunk,
  updateAdminSecondaryBranchesThunk,
  updateBranchOwnerThunk,
  setExchangeChargesMode,
  clearExchangeChargesError,
  resetExchangeChargesThunk,
  validateExchangeChargesThunk,
  copyExchangeChargesThunk,
  compareExchangeChargesThunk,
  getDefaultExchangeChargesThunk,
  updateExchangeChargesThunk,
  getExchangeChargesThunk,
} from "../thunks/branchThunks";

const initialState = {
  // Branch data
  branches: [], // all active branches
  allBranches: [], // all branches may including inactive
  selectedBranch: null,
  branchHierarchy: null,
  branchAnalytics: null,
  primaryBranch: null,

  // Separate pagination for active branches
  branchesPagination: {
    total: 0,
    totalPages: 1,
    currentPage: 1,
  },

  // Separate pagination for all branches
  allBranchesPagination: {
    total: 0,
    totalPages: 1,
    currentPage: 1,
  },

  // Store data
  stores: [],
  selectedStore: null,
  storeMetrics: null,

  // Pagination
  totalBranches: 0,
  totalPages: 1,
  currentPage: 1,

  // Loading states
  loadingList: false,
  loadingDetails: false,
  loading: false,

  // Error handling
  error: null,

  // Filters and config
  filters: {
    isActive: true,
    state: null,
    cityTier: null,
    branchType: null,
  },

  // Constants
  branchTypes: ["FLAGSHIP", "STANDARD", "FRANCHISE", "WAREHOUSE"],
  storeTypes: [
    "RETAIL",
    "MALL",
    "HIGH_STREET",
    "COMMUNITY",
    "AIRPORT",
    "HOTEL",
  ],
  cityTiers: ["METRO", "TIER1", "TIER2", "TIER3", "RURAL"],
  ratePolicies: ["INHERIT", "CUSTOM", "HYBRID"],
  rateModes: ["LIVE", "ADMIN", "HYBRID"],

  // Add hybrid policies to branch state
  hybridPolicies: {},

  inheritanceSources: [],
  inheritanceChain: [],
  dependentBranches: [],
  inheritanceValidation: {
    isValid: null,
    message: null,
    validating: false,
  },
  loadingInheritance: false,
  inheritanceError: null,

  // Exchange Charges state
  exchangeCharges: {
    current: null,
    defaults: null,
    comparison: null,
    isEditing: false,
    loadingExchange: false,
    exchangeError: null,
    validationResult: null,
  },
};

const branchSlice = createSlice({
  name: "branch",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setExchangeChargesEditing: (state, action) => {
      state.exchangeCharges.isEditing = action.payload;
    },
    clearExchangeChargesState: (state) => {
      state.exchangeCharges = {
        current: null,
        defaults: null,
        comparison: null,
        isEditing: false,
        loadingExchange: false,
        exchangeError: null,
        validationResult: null,
      };
    },
    setSelectedBranch: (state, action) => {
      const branchCode = action.payload;
      state.selectedBranch = branchCode;
    },
    setPrimaryBranch: (state, action) => {
      const primaryBranch = action.payload;
      state.primaryBranch = primaryBranch;
    },
  },
  extraReducers: (builder) => {
    builder
      // Clear error
      .addCase(clearBranchError, (state) => {
        state.error = null;
      })
      // Reset selected branch
      .addCase(resetSelectedBranch, (state) => {
        state.selectedBranch = null;
        state.stores = [];
      })
      // Reset store data
      .addCase(resetStoreData, (state) => {
        state.stores = [];
        state.selectedStore = null;
        state.storeMetrics = null;
      })

      // Create Branch
      .addCase(createBranchThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBranchThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.branches.unshift(action.payload.data);
        state.totalBranches += 1;
      })
      .addCase(createBranchThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Branches
      .addCase(getBranchesThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getBranchesThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.branches = action.payload.data;
        state.branchesPagination = {
          total: action.payload.pagination?.total || 0,
          totalPages: action.payload.pagination?.pages || 1,
          currentPage: action.payload.pagination?.page || 1,
        };
      })

      .addCase(getBranchesThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      // Get Branches
      .addCase(getAllBranchesThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAllBranchesThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.allBranches = action.payload.data;
        state.allBranchesPagination = {
          total: action.payload.pagination?.total || 0,
          totalPages: action.payload.pagination?.pages || 1,
          currentPage: action.payload.pagination?.page || 1,
        };
      })
      .addCase(getAllBranchesThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      // Get Single Branch
      .addCase(getBranchThunk.pending, (state) => {
        state.loadingDetails = true;
        state.error = null;
      })
      .addCase(getBranchThunk.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.selectedBranch = action.payload.data.branch;
        state.stores = action.payload.data.stores || [];
      })
      .addCase(getBranchThunk.rejected, (state, action) => {
        state.loadingDetails = false;
        state.error = action.payload;
      })

      // Update Branch
      .addCase(updateBranchThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBranchThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBranch = action.payload.data;
        const index = state.branches.findIndex(
          (b) => b._id === updatedBranch._id
        );
        if (index !== -1) {
          state.branches[index] = updatedBranch;
        }
        if (state.selectedBranch?._id === updatedBranch._id) {
          state.selectedBranch = updatedBranch;
        }
      })
      .addCase(updateBranchThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Configure Rate Mode
      .addCase(configureRateModeThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(configureRateModeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Set Main Branch
      .addCase(setMainBranchThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(setMainBranchThunk.fulfilled, (state, action) => {
        state.loading = false;
        const newMainBranch = action.payload.data;
        // Update all branches' main status
        state.branches = state.branches.map((branch) => ({
          ...branch,
          isMainBranch: branch._id === newMainBranch._id,
        }));
      })
      .addCase(setMainBranchThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Copy Configuration
      .addCase(copyConfigurationThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(copyConfigurationThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBranch = action.payload.data;
        const index = state.branches.findIndex(
          (b) => b._id === updatedBranch._id
        );
        if (index !== -1) {
          state.branches[index] = updatedBranch;
        }
      })
      .addCase(copyConfigurationThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Deactivate Branch
      .addCase(deactivateBranchThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deactivateBranchThunk.fulfilled, (state, action) => {
        state.loading = false;
        const deactivatedBranch = action.payload.data;
        const index = state.branches.findIndex(
          (b) => b._id === deactivatedBranch._id
        );
        if (index !== -1) {
          state.branches[index] = deactivatedBranch;
        }
      })
      .addCase(deactivateBranchThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Branch Analytics
      .addCase(getBranchAnalyticsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBranchAnalyticsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.branchAnalytics = action.payload.data;
      })
      .addCase(getBranchAnalyticsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Branch Hierarchy
      .addCase(getBranchHierarchyThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBranchHierarchyThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.branchHierarchy = action.payload.data;
      })
      .addCase(getBranchHierarchyThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Store
      .addCase(createStoreThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(createStoreThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.stores.push(action.payload.data);
      })
      .addCase(createStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Branch Stores
      .addCase(getBranchStoresThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBranchStoresThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.stores = action.payload.data;
      })
      .addCase(getBranchStoresThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Store
      .addCase(updateStoreThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateStoreThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedStore = action.payload.data;
        const index = state.stores.findIndex((s) => s._id === updatedStore._id);
        if (index !== -1) {
          state.stores[index] = updatedStore;
        }
        if (state.selectedStore?._id === updatedStore._id) {
          state.selectedStore = updatedStore;
        }
      })
      .addCase(updateStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Store
      .addCase(deleteStoreThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteStoreThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update to use storeId from the payload or meta
        const storeId = action.payload?.data?._id || action.meta.arg.storeId;
        state.stores = state.stores.filter((s) => s._id !== storeId);
      })
      .addCase(deleteStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Transfer Store
      .addCase(transferStoreThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(transferStoreThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Remove store from current branch's stores
        state.stores = state.stores.filter(
          (s) => s._id !== action.payload.data._id
        );
      })
      .addCase(transferStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Store Metrics
      .addCase(getStoreMetricsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getStoreMetricsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.storeMetrics = action.payload.data;
      })
      .addCase(getStoreMetricsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk Update Branches
      .addCase(bulkUpdateBranchesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkUpdateBranchesThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh branch list after bulk update
      })
      .addCase(bulkUpdateBranchesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk Deactivate Branches
      .addCase(bulkDeactivateBranchesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkDeactivateBranchesThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update deactivated branches in the list
      })
      .addCase(bulkDeactivateBranchesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add cases for configuring hybrid policy
      .addCase(configureHybridPolicyThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(configureHybridPolicyThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { branchId, response } = action.payload;
        const updatedBranch = response.data || response;

        // Store hybrid policy configuration
        state.hybridPolicies[branchId] =
          updatedBranch.hybridConfig || updatedBranch;

        // Update branch in branches list if present
        const index = state.branches.findIndex((b) => b._id === branchId);
        if (index !== -1) {
          state.branches[index] = {
            ...state.branches[index],
            rateConfig: {
              ...state.branches[index].rateConfig,
              ...updatedBranch.rateConfig,
            },
          };
        }

        // Update selected branch if it matches
        if (state.selectedBranch?._id === branchId) {
          state.selectedBranch = {
            ...state.selectedBranch,
            rateConfig: {
              ...state.selectedBranch.rateConfig,
              ...updatedBranch.rateConfig,
            },
          };
        }
      })
      .addCase(configureHybridPolicyThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      }) // Validate Inheritance
      .addCase(validateInheritanceThunk.pending, (state) => {
        state.inheritanceValidation.validating = true;
        state.inheritanceValidation.isValid = null;
        state.inheritanceValidation.message = null;
      })
      .addCase(validateInheritanceThunk.fulfilled, (state, action) => {
        state.inheritanceValidation.validating = false;
        state.inheritanceValidation.isValid = action.payload.valid;
        state.inheritanceValidation.message = action.payload.message;
      })
      .addCase(validateInheritanceThunk.rejected, (state, action) => {
        state.inheritanceValidation.validating = false;
        state.inheritanceValidation.isValid = false;
        state.inheritanceValidation.message = action.payload;
      })

      // Get Inheritance Sources
      .addCase(getInheritanceSourcesThunk.pending, (state) => {
        state.loadingInheritance = true;
        state.inheritanceError = null;
      })
      .addCase(getInheritanceSourcesThunk.fulfilled, (state, action) => {
        state.loadingInheritance = false;
        state.inheritanceSources = action.payload.data || [];
      })
      .addCase(getInheritanceSourcesThunk.rejected, (state, action) => {
        state.loadingInheritance = false;
        state.inheritanceError = action.payload;
        state.inheritanceSources = [];
      })

      // Get Inheritance Chain
      .addCase(getInheritanceChainThunk.pending, (state) => {
        state.loadingInheritance = true;
      })
      .addCase(getInheritanceChainThunk.fulfilled, (state, action) => {
        state.loadingInheritance = false;
        state.inheritanceChain = action.payload.data || [];
      })
      .addCase(getInheritanceChainThunk.rejected, (state, action) => {
        state.loadingInheritance = false;
        state.inheritanceChain = [];
      })

      // Get Dependent Branches
      .addCase(getDependentBranchesThunk.pending, (state) => {
        state.loadingInheritance = true;
      })
      .addCase(getDependentBranchesThunk.fulfilled, (state, action) => {
        state.loadingInheritance = false;
        state.dependentBranches = action.payload.data || [];
      })
      .addCase(getDependentBranchesThunk.rejected, (state, action) => {
        state.loadingInheritance = false;
        state.dependentBranches = [];
      })

      // Clear Inheritance Validation
      .addCase(clearInheritanceValidation, (state) => {
        state.inheritanceValidation = {
          isValid: null,
          message: null,
          validating: false,
        };
      })

      // Set Inheritance Source
      .addCase(setInheritanceSource, (state, action) => {
        if (state.selectedBranch) {
          state.selectedBranch.rateConfig = {
            ...state.selectedBranch.rateConfig,
            inheritFromBranch: action.payload,
          };
        }
      })
      .addCase(configureRateModeThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBranch = action.payload.data;
        const index = state.branches.findIndex(
          (b) => b._id === updatedBranch._id
        );
        if (index !== -1) {
          state.branches[index] = updatedBranch;
        }
        if (state.selectedBranch?._id === updatedBranch._id) {
          state.selectedBranch = updatedBranch;
        }
        // Clear inheritance validation after successful save
        state.inheritanceValidation = {
          isValid: null,
          message: null,
          validating: false,
        };
      })
      .addCase(updateBranchOwnerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBranchOwnerThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBranch = action.payload.data;

        // Update branch in branches list
        const index = state.branches.findIndex(
          (b) => b._id === updatedBranch._id
        );
        if (index !== -1) {
          state.branches[index] = {
            ...state.branches[index],
            ownerId: updatedBranch.ownerId,
            updatedBy: updatedBranch.updatedBy,
          };
        }

        // Update selected branch if it matches
        if (state.selectedBranch?._id === updatedBranch._id) {
          state.selectedBranch = {
            ...state.selectedBranch,
            ownerId: updatedBranch.ownerId,
            updatedBy: updatedBranch.updatedBy,
          };
        }
      })
      .addCase(updateBranchOwnerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Admin Primary Branch
      .addCase(updateAdminPrimaryBranchThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminPrimaryBranchThunk.fulfilled, (state, action) => {
        state.loading = false;
        // The response contains updated admin data
        // You might want to store this in a separate admin state or just show success
        // For now, we'll just clear loading state
      })
      .addCase(updateAdminPrimaryBranchThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Admin Secondary Branches
      .addCase(updateAdminSecondaryBranchesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminSecondaryBranchesThunk.fulfilled, (state, action) => {
        state.loading = false;
        // The response contains updated admin data with branchAccess
        // Store success state
      })
      .addCase(updateAdminSecondaryBranchesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      }) // Get Exchange Charges
      .addCase(getExchangeChargesThunk.pending, (state) => {
        state.exchangeCharges.loadingExchange = true;
        state.exchangeCharges.exchangeError = null;
      })
      .addCase(getExchangeChargesThunk.fulfilled, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.current = action.payload.data;
      })
      .addCase(getExchangeChargesThunk.rejected, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.exchangeError = action.payload;
      })

      // Update Exchange Charges
      .addCase(updateExchangeChargesThunk.pending, (state) => {
        state.exchangeCharges.loadingExchange = true;
        state.exchangeCharges.exchangeError = null;
      })
      .addCase(updateExchangeChargesThunk.fulfilled, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.current = action.payload.data;
        state.exchangeCharges.isEditing = false;

        // Update the branch's exchange charges if it's the selected branch
        if (
          state.selectedBranch &&
          state.selectedBranch._id === action.meta.arg.branchId
        ) {
          state.selectedBranch.settings = {
            ...state.selectedBranch.settings,
            exchangeCharges: action.payload.data.exchangeCharges,
          };
        }
      })
      .addCase(updateExchangeChargesThunk.rejected, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.exchangeError = action.payload;
      })

      // Get Default Exchange Charges
      .addCase(getDefaultExchangeChargesThunk.pending, (state) => {
        state.exchangeCharges.loadingExchange = true;
      })
      .addCase(getDefaultExchangeChargesThunk.fulfilled, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.defaults = action.payload.data;
      })
      .addCase(getDefaultExchangeChargesThunk.rejected, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.exchangeError = action.payload;
      })

      // Compare Exchange Charges
      .addCase(compareExchangeChargesThunk.pending, (state) => {
        state.exchangeCharges.loadingExchange = true;
      })
      .addCase(compareExchangeChargesThunk.fulfilled, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.comparison = action.payload.data;
      })
      .addCase(compareExchangeChargesThunk.rejected, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.exchangeError = action.payload;
      })

      // Copy Exchange Charges
      .addCase(copyExchangeChargesThunk.pending, (state) => {
        state.exchangeCharges.loadingExchange = true;
      })
      .addCase(copyExchangeChargesThunk.fulfilled, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        // Optionally update branches that were copied to
      })
      .addCase(copyExchangeChargesThunk.rejected, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.exchangeError = action.payload;
      })

      // Validate Exchange Charges
      .addCase(validateExchangeChargesThunk.pending, (state) => {
        state.exchangeCharges.loadingExchange = true;
      })
      .addCase(validateExchangeChargesThunk.fulfilled, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.validationResult = action.payload;
      })
      .addCase(validateExchangeChargesThunk.rejected, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.validationResult = {
          valid: false,
          errors: [action.payload],
        };
      })

      // Reset Exchange Charges
      .addCase(resetExchangeChargesThunk.pending, (state) => {
        state.exchangeCharges.loadingExchange = true;
      })
      .addCase(resetExchangeChargesThunk.fulfilled, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.current = action.payload.data;

        // Update the branch's exchange charges if it's the selected branch
        if (
          state.selectedBranch &&
          state.selectedBranch._id === action.meta.arg
        ) {
          state.selectedBranch.settings = {
            ...state.selectedBranch.settings,
            exchangeCharges: action.payload.data.exchangeCharges,
          };
        }
      })
      .addCase(resetExchangeChargesThunk.rejected, (state, action) => {
        state.exchangeCharges.loadingExchange = false;
        state.exchangeCharges.exchangeError = action.payload;
      })

      // Clear Exchange Charges Error
      .addCase(clearExchangeChargesError, (state) => {
        state.exchangeCharges.exchangeError = null;
        state.exchangeCharges.validationResult = null;
      })

      // Set Exchange Charges Mode
      .addCase(setExchangeChargesMode, (state, action) => {
        state.exchangeCharges.isEditing = action.payload;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentPage,
  setSelectedBranch,
  setPrimaryBranch,
} = branchSlice.actions;
export default branchSlice.reducer;
